import { AvailableStaff } from '@spike/available-staff-model';
import { InvoiceTipDistribution, ManualTipInput } from '@spike/invoice-model';
import { InvoicesStatus, saveManualTipsThunk } from '@spike/invoices-action';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { useApiClientWrapper } from 'hooks';
import { Staff } from 'model/Staff';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { v4 as uuidv4 } from 'uuid';
import { DistributionRow } from './types';

interface UseTipDistributionOptions {
    invoiceId: number;
    tipDistribution: Array<InvoiceTipDistribution>;
    baseStaffIds: ReadonlySet<number>;
    tipsManual?: boolean;
    tipResetTrigger?: number;
    clearAddedRowsTrigger?: number;
    redistributeKey?: number;
    redistributeAmount?: string;
    initialAddedRowAmount?: string;
    onSaving?: (saving: boolean) => void;
    onTotalChange?: (total: string) => void;
    onRowAdded?: () => void;
    onHasAddedRowsChange?: (hasAdded: boolean) => void;
}

interface UseTipDistributionResult {
    rows: Array<DistributionRow>;
    hasUnassignedAddedRow: boolean;
    hasAssignedStaff: boolean;
    noStaffLeft: boolean;
    isSaving: boolean;
    hasSettled: boolean;
    hasHadRows: boolean;
    buildAvailableStaffFor: (rowUuid: string) => Array<AvailableStaff>;
    handleAmountChange: (rowUuid: string, amount: string) => void;
    handleSelectStaff: (rowUuid: string, staff: AvailableStaff) => void;
    handleRemove: (rowUuid: string) => void;
    handleAddRow: () => void;
}

const fromInvoiceDistribution = (d: InvoiceTipDistribution, baseStaffIds: ReadonlySet<number>): DistributionRow => ({
    id: d.id ?? null,
    uuid: d.uuid,
    staffId: d.staff.id,
    staffName: d.staff.name ?? null,
    staffImageUrl: d.staff.imageUrl ?? null,
    amount: Number(d.amount || 0).toFixed(2),
    source: baseStaffIds.has(d.staff.id) ? 'original' : 'added'
});

const toAvailableStaff = (s: Staff): AvailableStaff => ({
    id: s.id!,
    firstName: s.person.firstName,
    lastName: s.person.lastName,
    avatar: s.person.avatar
});

const computeTotal = (rows: Array<DistributionRow>): string =>
    rows.reduce((acc, r) => acc + Number(r.amount || 0), 0).toFixed(2);

export const useTipDistribution = (options: UseTipDistributionOptions): UseTipDistributionResult => {
    const {
        invoiceId,
        tipDistribution,
        baseStaffIds,
        tipsManual,
        tipResetTrigger,
        clearAddedRowsTrigger,
        redistributeKey,
        redistributeAmount,
        initialAddedRowAmount,
        onSaving,
        onTotalChange,
        onRowAdded,
        onHasAddedRowsChange
    } = options;

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const allStaff = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const [pendingSave, setPendingSave] = useState(false);
    const [rows, setRows] = useState<Array<DistributionRow>>(() =>
        tipDistribution.map(d => fromInvoiceDistribution(d, baseStaffIds))
    );
    const [hasSettled, setHasSettled] = useState(() => tipDistribution.length > 0);
    const pendingClearAddedRef = useRef(false);
    // Tracks whether the distribution has ever held rows. Once it has, an empty list is a real
    // resolved state (e.g. the user deleted everything) — it should render empty + warning, never
    // a skeleton or an auto-seed.
    const hasHadRowsRef = useRef(tipDistribution.length > 0);

    useEffect(() => {
        if (allStaff.length === 0) {
            dispatch(fetchStaffThunk());
        }
    }, [allStaff.length, dispatch]);

    useEffect(() => {
        if (rows.length > 0) hasHadRowsRef.current = true;
    }, [rows.length]);

    useEffect(() => {
        setRows(prev => {
            // The backend tips array always contains every invoice-line staff member (and, in
            // manual mode, any manually added staff). Dedup it by staffId up front so a staff can
            // never be represented by two rows.
            const serverRows = tipDistribution.map(d => fromInvoiceDistribution(d, baseStaffIds));
            const dedupedServer: Array<DistributionRow> = [];
            const serverSeenStaff = new Set<number>();
            for (const r of serverRows) {
                if (r.staffId !== null) {
                    if (serverSeenStaff.has(r.staffId)) continue;
                    serverSeenStaff.add(r.staffId);
                }
                dedupedServer.push(r);
            }

            const clearAdded = pendingClearAddedRef.current;
            pendingClearAddedRef.current = false;

            // Automatic distribution: the backend array is authoritative — render it as-is.
            // Only keep a still-unassigned added row the user is mid-creating (before the staff
            // is picked and tipsManual flips to true), so the in-progress add isn't wiped.
            if (!tipsManual) {
                const pendingUnassigned = clearAdded
                    ? []
                    : prev.filter(r => r.source === 'added' && r.staffId === null);
                return [...dedupedServer, ...pendingUnassigned];
            }

            // Manual distribution: reconcile local rows against the array by uuid, then fall back
            // to staffId for added rows (the backend may re-key a just-added row with a new uuid),
            // and dedup the result by staffId.
            const serverByUuid = new Map(dedupedServer.map(r => [r.uuid, r]));
            const serverByStaffId = new Map(
                dedupedServer.filter(r => r.staffId !== null).map(r => [r.staffId as number, r])
            );

            const consumedUuids = new Set<string>();
            const seenStaffIds = new Set<number>();
            const reconciled: Array<DistributionRow> = [];

            const pushUnique = (row: DistributionRow) => {
                if (row.staffId !== null) {
                    if (seenStaffIds.has(row.staffId)) return;
                    seenStaffIds.add(row.staffId);
                }
                reconciled.push(row);
            };

            for (const row of prev) {
                const byUuid = serverByUuid.get(row.uuid);
                if (byUuid) {
                    consumedUuids.add(byUuid.uuid);
                    pushUnique({ ...byUuid, source: row.source });
                    continue;
                }

                if (row.source === 'added' && row.staffId !== null) {
                    const byStaff = serverByStaffId.get(row.staffId);
                    if (byStaff && !consumedUuids.has(byStaff.uuid)) {
                        consumedUuids.add(byStaff.uuid);
                        pushUnique({ ...byStaff, source: 'added' });
                        continue;
                    }
                }

                // Keep optimistic added rows (unassigned or mid-save) unless we're clearing them.
                // Original rows absent from the server array are dropped.
                if (!clearAdded && row.source === 'added') {
                    pushUnique(row);
                }
            }

            for (const serverRow of dedupedServer) {
                if (consumedUuids.has(serverRow.uuid)) continue;
                pushUnique(serverRow);
            }

            return reconciled;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipDistribution]);

    useNonInitialEffect(() => {
        pendingClearAddedRef.current = true;
        // Drop only not-yet-persisted added rows (an in-progress add). Persisted rows stay
        // visible with the loading state and are replaced once the backend response arrives,
        // so switching tips never makes existing distribution rows disappear mid-request.
        setRows(prev => prev.filter(r => r.source !== 'added' || r.id !== null));
    }, [clearAddedRowsTrigger]);

    useNonInitialEffect(() => {
        if (!pendingSave) return;
        if (invoicesStatus === InvoicesStatus.SaveTipSuccess || invoicesStatus === InvoicesStatus.Error) {
            setPendingSave(false);
            onSaving?.(false);
        }
    }, [invoicesStatus]);

    useEffect(() => {
        if (hasSettled) return;

        const waitingForRows =
            pendingSave || // a save is in flight; rows will follow
            tipDistribution.length > 0 || // rows about to sync from props
            allStaff.length === 0; // staff list still loading — can't offer "Add Staff Member" yet

        if (rows.length > 0 || !waitingForRows) {
            setHasSettled(true);
        }
    }, [hasSettled, rows.length, pendingSave, tipDistribution.length, allStaff.length]);

    useNonInitialEffect(() => {
        setRows(prev => {
            const addedRows = prev.filter(r => r.source === 'added');
            if (addedRows.length === 0) return prev;

            const assignedAdded = addedRows.filter(r => r.staffId !== null);

            if (assignedAdded.length > 0) {
                const inputs: ManualTipInput[] = assignedAdded.map(r =>
                    r.id !== null ? { id: r.id, amount: '0.00' } : { staffId: r.staffId!, amount: '0.00', uuid: r.uuid }
                );
                setPendingSave(true);
                onSaving?.(true);
                dispatch(saveManualTipsThunk(apiClientWrapper, invoiceId, inputs));
            }

            const next = prev.map(r => (r.source === 'added' ? { ...r, amount: '0.00' } : r));
            onTotalChange?.(computeTotal(next));
            return next;
        });
    }, [tipResetTrigger]);

    useNonInitialEffect(() => {
        if (redistributeAmount === undefined) return;

        setRows(prev => {
            const originalRows = prev.filter(r => r.source === 'original');
            const addedRows = prev.filter(r => r.source === 'added');
            const newTotal = Number(redistributeAmount);
            const newAmounts = new Map<string, string>();

            if (originalRows.length === 0 && addedRows.length > 0) {
                // All rows are added — distribute equally among them
                let remaining = newTotal;
                addedRows.slice(0, -1).forEach(r => {
                    const amt = Math.round((newTotal / addedRows.length) * 100) / 100;
                    remaining = Math.round((remaining - amt) * 100) / 100;
                    newAmounts.set(r.uuid, amt.toFixed(2));
                });
                newAmounts.set(addedRows[addedRows.length - 1].uuid, remaining.toFixed(2));
            } else {
                const originalTotal = originalRows.reduce((sum, r) => sum + Number(r.amount), 0);
                if (originalTotal === 0 || originalRows.length === 0) {
                    const perRow = originalRows.length > 0 ? newTotal / originalRows.length : 0;
                    originalRows.forEach(r => newAmounts.set(r.uuid, perRow.toFixed(2)));
                } else {
                    let remaining = newTotal;
                    originalRows.slice(0, -1).forEach(r => {
                        const amt = Math.round((Number(r.amount) / originalTotal) * newTotal * 100) / 100;
                        remaining = Math.round((remaining - amt) * 100) / 100;
                        newAmounts.set(r.uuid, amt.toFixed(2));
                    });
                    newAmounts.set(originalRows[originalRows.length - 1].uuid, remaining.toFixed(2));
                }
            }

            const next = prev.map(r => ({
                ...r,
                amount: newAmounts.get(r.uuid) ?? (r.source === 'added' ? '0.00' : r.amount)
            }));

            const inputs: ManualTipInput[] = next
                .filter(r => r.staffId !== null)
                .map(r =>
                    r.id !== null
                        ? { id: r.id, amount: r.amount }
                        : { staffId: r.staffId!, amount: r.amount, uuid: r.uuid }
                );

            if (inputs.length > 0) {
                setPendingSave(true);
                onSaving?.(true);
                dispatch(saveManualTipsThunk(apiClientWrapper, invoiceId, inputs));
            }

            onTotalChange?.(computeTotal(next));
            return next;
        });
    }, [redistributeKey]);

    const activeStaff = useMemo<Array<Staff>>(
        () => allStaff.filter(s => s.active && !s.deleted && s.id !== undefined),
        [allStaff]
    );

    const buildAvailableStaffFor = useCallback(
        (rowUuid: string): Array<AvailableStaff> => {
            const usedIds = new Set(
                rows.filter(r => r.uuid !== rowUuid && r.staffId !== null).map(r => r.staffId as number)
            );
            return activeStaff.filter(s => !usedIds.has(s.id!)).map(toAvailableStaff);
        },
        [activeStaff, rows]
    );

    const dispatchManualTip = (input: ManualTipInput) => {
        setPendingSave(true);
        onSaving?.(true);
        dispatch(saveManualTipsThunk(apiClientWrapper, invoiceId, [input]));
    };

    const handleAmountChange = (rowUuid: string, amount: string) => {
        const target = rows.find(r => r.uuid === rowUuid);
        if (!target) return;

        const next = rows.map(r => (r.uuid === rowUuid ? { ...r, amount } : r));
        setRows(next);
        onTotalChange?.(computeTotal(next));

        if (target.staffId === null) return;

        if (target.id !== null) {
            dispatchManualTip({ id: target.id, staffId: target.staffId, amount });
        } else {
            dispatchManualTip({ staffId: target.staffId, amount, uuid: target.uuid });
        }
    };

    const handleSelectStaff = (rowUuid: string, staff: AvailableStaff) => {
        const target = rows.find(r => r.uuid === rowUuid);
        if (!target) return;

        const next = rows.map(r =>
            r.uuid === rowUuid
                ? {
                      ...r,
                      staffId: staff.id,
                      staffName: `${staff.firstName} ${staff.lastName}`.trim(),
                      staffImageUrl: staff.avatar || null
                  }
                : r
        );
        setRows(next);

        if (target.id === null) {
            dispatchManualTip({ staffId: staff.id, amount: target.amount, uuid: target.uuid });
        }
    };

    const handleRemove = (rowUuid: string) => {
        const target = rows.find(r => r.uuid === rowUuid);
        if (!target) return;

        const next = rows.filter(r => r.uuid !== rowUuid);
        setRows(next);
        onTotalChange?.(computeTotal(next));

        if (target.id !== null) {
            dispatchManualTip({ id: target.id, destroy: true });
        }
    };

    const handleAddRow = () => {
        setRows(prev => {
            const hasAssigned = prev.some(r => r.staffId !== null);
            const amount = !hasAssigned && initialAddedRowAmount ? initialAddedRowAmount : '0.00';
            return [
                ...prev,
                {
                    id: null,
                    uuid: uuidv4(),
                    staffId: null,
                    staffName: null,
                    staffImageUrl: null,
                    amount,
                    source: 'added'
                }
            ];
        });
        onRowAdded?.();
    };

    const hasAddedRows = useMemo(() => rows.some(r => r.source === 'added'), [rows]);

    useEffect(() => {
        onHasAddedRowsChange?.(hasAddedRows);
    }, [hasAddedRows]);

    const sortedRows = useMemo<Array<DistributionRow>>(
        () =>
            [...rows].sort((a, b) => {
                if (a.source !== b.source) return a.source === 'original' ? -1 : 1;
                if (a.staffName === null) return 1;
                if (b.staffName === null) return -1;
                return a.staffName.localeCompare(b.staffName);
            }),
        [rows]
    );

    const hasUnassignedAddedRow = rows.some(r => r.source === 'added' && r.staffId === null);
    const hasAssignedStaff = rows.some(r => r.staffId !== null);
    const noStaffLeft = activeStaff.length === 0 || activeStaff.every(s => rows.some(r => r.staffId === s.id));

    return {
        rows: sortedRows,
        hasUnassignedAddedRow,
        hasAssignedStaff,
        noStaffLeft,
        isSaving: pendingSave,
        hasSettled,
        hasHadRows: hasHadRowsRef.current,
        buildAvailableStaffFor,
        handleAmountChange,
        handleSelectStaff,
        handleRemove,
        handleAddRow
    };
};
